import { VideoSourceSection, VideoSourceSectionProps } from "./VideoSourceSection";
import { Controller, useFormContext } from "react-hook-form";
import { FormFieldError } from "@kaltura/mediaspace-shared-ui/form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useRef, useState } from "react";
import { UploadedMediaData } from "./types";

export interface ControlledVideoSourceSectionProps extends VideoSourceSectionProps {
    /** Form field ID */
    id: string;
}

/**
 * Simulive video source section for the Streaming tab and Simulive creation modal.
 * Integrated with react-hook-form.
 */
export const ControlledVideoSourceSection = ({ id, ...props }: ControlledVideoSourceSectionProps) => {
    const { control, formState, trigger } = useFormContext();

    /**
     * The field uses manual field re-validation (trigger) to update the processing state validation rules.
     * With react-hook-form's behavior, it makes the error message appear immediately, even in the normal workflow.
     * The only solution is to track the field value and submitting the form, and hide the error message manually.
     */
    const [changedOnce, setChangedOnce] = useState(false);

    const isProcessingRef = useRef(false);

    const handleProcessingChange = (processing: boolean) => {
        isProcessingRef.current = processing;
        // Recalculate field's validation rules
        void trigger(id);
    };

    const validate = (value: unknown) => {
        // The processing error message is superior to the required field validation - check it first
        if (isProcessingRef.current) {
            return translate("Video asset is still processing");
        }

        // Required field validation (done manually to set the correct rules order)
        return !!value;
    };

    return (
        <Controller
            control={control}
            name={id}
            defaultValue={props.initialMedia?.entryId}
            rules={{ validate }}
            render={({ field: { ref, onChange, onBlur }, fieldState: { error } }) => (
                <div>
                    <VideoSourceSection
                        {...props}
                        buttonRef={ref}
                        onDataChange={(data: UploadedMediaData | undefined) => {
                            onChange(data?.entryId);
                            setChangedOnce(true);
                        }}
                        onProcessingChange={handleProcessingChange}
                        onBlur={onBlur}
                    />

                    {error && (changedOnce || formState.isSubmitted) && (
                        <FormFieldError>
                            {error.message
                                || translate("Please select a video source in order to create a pre-recorded session")}
                        </FormFieldError>
                    )}
                </div>
            )}
        />
    );
};
